import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import axios from '../utilities/axios';
import * as API_END_POINT from '../utilities/constant';
import { currentColorOf } from '../shared/theme/storefrontTheme';
import { buildDefaultHomePage } from '../pages/StoreBuilder/defaultHomePage';

const StoreContext = createContext();

export const StoreProvider = ({ children }) => {
    const [publishedPage, setPublishedPage] = useState(null);
    // undefined while loading; false when the request succeeded with no published page.
    const [pagePublished, setPagePublished] = useState(undefined);
    const [blockTemplates, setBlockTemplates] = useState(null);
    const [storeDetails, setStoreDetails] = useState(null);
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [shouldRedirectToComingSoon, setShouldRedirectToComingSoon] = useState(false);
    const [customerInfo, setCustomerInfo] = useState(() => {
        const savedCustomerInfo = localStorage.getItem('customerInfo');
        return savedCustomerInfo ? JSON.parse(savedCustomerInfo) : {
            contactEmail: '',
            firstName: '',
            lastName: '',
            addressLine1: '',
            addressLine2: '',
            country: { value: 1, label: "United States" },
            state: '',
            city: '',
            zip: '',
            phoneNumber: ''
        };
    });
    const [brandsCartCalc, setBrandsCartCalc] = useState([]);
    const [storeOrderStatus, setStoreOrderStatus] = useState([
    ]);
    const [headerMenuCategories, setHeaderMenuCategories] = useState([]);

    const getMenucatgories = async () => {
        try {
            const response = await axios
                .post(API_END_POINT.STORE_MENU_CATEGORY)
            setHeaderMenuCategories(response.data.data?.categories);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };
    const getStoreDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(API_END_POINT.STORE_DETAILS);
            if (response && response.data && response.data.code === 200) {
                setStoreDetails(response.data.data);
                await getCart();
            }
        } catch (err) {
            if (err.response?.status === 401) {
                setShouldRedirectToComingSoon(true);
            }
            setError(err);
        } finally {
            setLoading(false);
        }
    };
    const getPageDetails = async () => {
        try {
            const response = await axios
                .get(API_END_POINT.RETAILER_STORE_BUILDER(new URLSearchParams({
                    "page_type": "HOME",
                }).toString()));
            const data = response.data.data;
            // A retired color (Cream) shows as its replacement everywhere on the storefront.
            const appearance = data?.page_data?.colorAndApperances;
            if (appearance?.colorTheme) appearance.colorTheme = currentColorOf(appearance.colorTheme);
            setPublishedPage(data);
            setPagePublished(Boolean(data));
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const getBlockTemplates = async () => {
        try {
            const response = await axios.get(API_END_POINT.RETAILER_BLOCK_TEMPLATES);
            console.log('Block templates response:', response);
            setBlockTemplates(response.data.data);
        } catch (err) {
            console.warn('[StoreContext] Failed to fetch block templates, falling back to page_data.blockManager', err);
        }
    };

    const addToCart = async (data) => {
        try {
            setLoading(true);
            data.device_id = localStorage.getItem('device_id');
            const response = await axios.post(API_END_POINT.STORE_ADD_TO_CART, data);

            if (response && response.data && response.data.code === 201) {
                await getCart();
            }
        } catch (err) {
            console.log('Failed to add item to cart');
            console.log(err)
        } finally {
            setLoading(false);
        }
    };

    const getCart = async (zipcode = '') => {
        try {
            setLoading(true);
            let deviceId = localStorage.getItem('device_id');
            const response = await axios.get(API_END_POINT.STORE_CART_DATA(deviceId, zipcode));

            if (response && response.data && response.data.code === 200) {
                setCartItems(response.data.data);
            }
        } catch (err) {
            console.log('Failed to fetch cart data');
        } finally {
            setLoading(false);
        }
    };

    const removeCartProduct = async (data) => {
        try {
            setLoading(true);
            const device_id = localStorage.getItem('device_id');
            const config = {
                data: { ...data, device_id },
            };
            await axios.delete(API_END_POINT.STORE_CART_REMOVE_PRODUCT, config);
            await getCart();
        } catch (err) {
            console.log('Failed to remove product from cart');
        } finally {
            setLoading(false);
        }
    }
    // A request error leaves this null rather than standing in the default, so a
    // transient failure never replaces a real designed page.
    const pageDetails = useMemo(() => {
        if (publishedPage) return publishedPage;
        if (pagePublished === false) return buildDefaultHomePage(storeDetails);
        return null;
    }, [publishedPage, pagePublished, storeDetails]);

    useEffect(() => {
        getStoreDetails();
        getPageDetails();
        getBlockTemplates();
    }, []);

    useEffect(() => {
        getCart(customerInfo.zip);
    }, [customerInfo, storeOrderStatus])

    useEffect(() => {
        localStorage.setItem('customerInfo', JSON.stringify(customerInfo));
    }, [customerInfo]);
    useEffect(() => {
        getMenucatgories();
    }, []);
    return (
        <StoreContext.Provider value={{
            storeDetails,
            cartItems,
            loading,
            error,
            shouldRedirectToComingSoon,
            getStoreDetails,
            addToCart,
            getCart,
            removeCartProduct,
            customerInfo,
            setCustomerInfo,
            setBrandsCartCalc,
            brandsCartCalc,
            setStoreOrderStatus,
            storeOrderStatus,
            pageDetails,
            blockTemplates,
            headerMenuCategories
        }}>
            {children}
        </StoreContext.Provider>
    );
};

export const useStore = () => {
    const context = useContext(StoreContext);
    if (!context) {
        throw new Error('useStore must be used within a StoreProvider');
    }
    return context;
};

export default { StoreProvider, useStore };